'use client'

import { useEffect, useId, useRef, useState } from 'react'

import {
  newSessionToken,
  placeDetails,
  placesBrowserKey,
  suggest,
  type Place,
  type Suggestion,
} from '@/lib/google-places'

type Props = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'role' | 'aria-expanded' | 'aria-controls'
> & {
  value: string
  /** Called for every keystroke, as a plain input's onChange would. */
  onValueChange: (value: string) => void
  /** Called once with the chosen address filled in. Typing alone never calls it. */
  onPlace: (place: Place) => void
  /** Class names for the pieces, so each form keeps its own look. */
  listClassName?: string
  optionClassName?: string
  activeOptionClassName?: string
  /** For tests: the key, in place of the environment's. */
  apiKey?: string
}

const DEBOUNCE_MS = 250

/**
 * A text input that offers Australian address suggestions while someone types. With no browser
 * key it is exactly the input it wraps, so the forms work as they always did. A search that is
 * slow, empty or refused simply shows no list, and whatever was typed stays.
 */
export function AddressAutocomplete({
  value,
  onValueChange,
  onPlace,
  listClassName,
  optionClassName,
  activeOptionClassName,
  apiKey,
  ...inputProps
}: Props) {
  const key = apiKey ?? placesBrowserKey()
  const listId = useId()
  const [options, setOptions] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  // One token for a whole search, from the first keystroke to the choice. A new search gets a new one.
  const session = useRef<string | null>(null)
  const abort = useRef<AbortController | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const typed = useRef(false)

  useEffect(
    () => () => {
      abort.current?.abort()
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )

  useEffect(() => {
    if (!key || !typed.current) return
    if (timer.current) clearTimeout(timer.current)
    abort.current?.abort()
    if (value.trim().length < 3) {
      setOptions([])
      return
    }
    timer.current = setTimeout(async () => {
      const controller = new AbortController()
      abort.current = controller
      session.current ??= newSessionToken()
      const found = await suggest(value, session.current, key, { signal: controller.signal })
      if (controller.signal.aborted) return
      setOptions(found)
      setActive(-1)
      setOpen(found.length > 0)
    }, DEBOUNCE_MS)
  }, [value, key])

  if (!key) {
    return <input {...inputProps} value={value} onChange={(e) => onValueChange(e.target.value)} />
  }

  const choose = async (option: Suggestion) => {
    const token = session.current ?? newSessionToken()
    setOpen(false)
    setOptions([])
    typed.current = false
    // The choice ends the search whether or not Google answers; the next one starts afresh.
    session.current = null
    const place = await placeDetails(option.placeId, token, key)
    if (place) onPlace(place)
    else onValueChange(option.text.replace(/, Australia$/, ''))
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        {...inputProps}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        onChange={(e) => {
          typed.current = true
          onValueChange(e.target.value)
        }}
        onKeyDown={(e) => {
          if (!open) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => (i + 1) % options.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => (i <= 0 ? options.length - 1 : i - 1))
          } else if (e.key === 'Enter' && active >= 0) {
            e.preventDefault()
            void choose(options[active])
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className={listClassName}
          style={{
            position: 'absolute',
            zIndex: 20,
            left: 0,
            right: 0,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            background: '#fff',
            color: '#1a0e20',
            border: '1px solid #b9b0c0',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {options.map((option, i) => (
            <li
              key={option.placeId}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? activeOptionClassName : optionClassName}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                background: i === active ? '#f0eaf5' : undefined,
              }}
              onMouseDown={(e) => {
                // Before the input loses focus, or the list would close under the click.
                e.preventDefault()
                void choose(option)
              }}
            >
              <strong>{option.main}</strong>
              {option.secondary && <span style={{ opacity: 0.7 }}> {option.secondary}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
